import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
    addDays,
    differenceInCalendarDays,
    endOfDay,
    isSameDay,
    secondsToMilliseconds,
    startOfDay,
} from 'date-fns';

import {
    EPGAlignment,
    EPGChannels,
    EPGConfig,
    IEPG,
} from 'custom_modules/react-native-epg';

import { SCREEN } from 'router/screens';

import { setFocusToNow } from 'store/epg/actions';
import { EpgRequestedBy } from 'store/epg/reducer';
import { selectEpgParams, selectEpgRequestStatus } from 'store/epg/selectors';
import { requestSuccess } from 'store/utils';

import usePrevious from 'utils/hooks/usePrevious';
import { navigationRef } from 'utils/navigation/navigationUtils';
import { isWeb } from 'utils/responsiveness';

import { CONFIG } from 'screens/Epg/utils/config';

import { EpgChannelExtra } from '../types';

type Props = {
    epgRef: MutableRefObject<IEPG | null>;
    config: Partial<EPGConfig>;
    focused: boolean;
    focusToNow: boolean;
    channelId?: string;
    isEPGSelectedChannel: boolean;
    fullscreen: boolean;
    channels: EPGChannels<EpgChannelExtra>;
};

const useAutoScrollOnTimeline = ({
    epgRef,
    config,
    focused,
    focusToNow,
    channelId,
    isEPGSelectedChannel,
    fullscreen,
    channels,
}: Props) => {
    const { start: epgDataStartTime, requestedBy } =
        useSelector(selectEpgParams);
    const status = useSelector(selectEpgRequestStatus);
    const dispatch = useDispatch();

    const startTime = secondsToMilliseconds(epgDataStartTime);
    const prevStarTime = usePrevious(startTime, undefined);
    const prevHoursCount = usePrevious(config.hoursCount, CONFIG.hoursCount);

    const scrollToTheEndOfTheDay = useCallback((newDay: Date) => {
        epgRef.current?.scrollToTime(
            endOfDay(newDay).getTime(),
            EPGAlignment.end,
            false,
        );
    }, []);

    /**
     * useEffect hook used to handle the scrolling on the timeline. */
    useEffect(() => {
        const now = new Date();
        const newDay = addDays(now, differenceInCalendarDays(startTime, now));

        /**
         * If the new date is requested by the previous day jump marker, the epg needs to be scrolled to the end of
         * the new day without animation. */
        if (requestedBy === EpgRequestedBy.PREV_DAY_JUMP_MARKER) {
            if (config.hoursCount !== prevHoursCount) {
                setTimeout(() => {
                    scrollToTheEndOfTheDay(newDay);
                }, 1000);
            } else {
                setTimeout(() => {
                    scrollToTheEndOfTheDay(newDay);
                }, 300);
            }
        }

        /**
         * If the new date is requested by the next day jump marker, the epg needs to be scrolled to the start of
         * the new day without animation. */
        if (requestedBy === EpgRequestedBy.NEXT_DAY_JUMP_MARKER) {
            setTimeout(() => {
                epgRef.current?.scrollToTime(
                    startOfDay(newDay).getTime(),
                    EPGAlignment.start,
                    false,
                );
            }, 300);
        }

        /**
         * If the selected date is the same day as the current date,
         * and it was not requested by prev or next day jump
         * markers, it needs to scroll to now. */
        if (
            prevStarTime &&
            prevStarTime !== startTime &&
            isSameDay(startTime, now) &&
            !(
                requestedBy === EpgRequestedBy.NEXT_DAY_JUMP_MARKER ||
                requestedBy === EpgRequestedBy.PREV_DAY_JUMP_MARKER
            )
        ) {
            setTimeout(() => {
                epgRef.current?.scrollToNow();
            }, 300);
        }
    }, [
        startTime,
        requestedBy,
        prevStarTime,
        config.hoursCount,
        scrollToTheEndOfTheDay,
    ]);

    const prevFocused = useRef(focused);
    const previousRouteRef = useRef<string | undefined>('');
    const previousChannelRef = useRef<string | undefined>(channelId);

    useEffect(() => {
        if (
            focusToNow &&
            !fullscreen &&
            channelId &&
            channelId !== previousChannelRef.current &&
            !isWeb
        ) {
            setTimeout(() => {
                epgRef.current?.scrollToNow();
            }, 600);
            setTimeout(() => {
                epgRef.current?.scrollToChannel(channelId);
                previousChannelRef.current = channelId;
            }, 410);
        }
    }, [channelId, fullscreen, focusToNow]);

    /**
     * useEffect used to scroll to now when accessing the page for the first time. */
    useEffect(() => {
        const currentRoute = navigationRef.current?.getCurrentRoute()?.name;

        if (previousRouteRef.current === SCREEN.EPG) {
            previousRouteRef.current = currentRoute;
        }

        if (
            focused &&
            requestSuccess(status) &&
            requestedBy === EpgRequestedBy.SCREEN_ACCESS
        ) {
            if (focusToNow) {
                setTimeout(() => {
                    epgRef.current?.scrollToNow();

                    if (isWeb) {
                        setTimeout(() => {
                            // To fix scroll reset to initial value on web
                            dispatch(setFocusToNow(false));
                        }, 410);
                    }
                }, 600);

                if (channelId) {
                    setTimeout(() => {
                        epgRef.current?.scrollToChannel(channelId);
                    }, 410);
                }
            }

            if (!focusToNow) {
                const isFromSameStack =
                    previousRouteRef.current === SCREEN.SETTINGS ||
                    previousRouteRef.current === SCREEN.SEARCH ||
                    previousRouteRef.current === SCREEN.PROFILES;

                /** logic to respect REAC-383 */
                if (
                    !isFromSameStack &&
                    !isEPGSelectedChannel &&
                    !fullscreen &&
                    channelId &&
                    channelId !== previousChannelRef.current
                ) {
                    setTimeout(() => {
                        epgRef.current?.scrollToChannel(channelId);
                    }, 410);

                    setTimeout(() => {
                        epgRef.current?.scrollToNow();
                    }, 600);

                    return;
                }

                if (isWeb) {
                    setTimeout(() => {
                        epgRef.current?.restoreScrollPosition();
                    }, 600);
                }
            }
        } else if (!prevFocused.current && focused && isWeb) {
            setTimeout(() => {
                epgRef.current?.restoreScrollPosition();
            }, 400);
        }

        prevFocused.current = focused;
        previousRouteRef.current = currentRoute;
    }, [
        channels,
        focusToNow,
        focused,
        isEPGSelectedChannel,
        requestedBy,
        status,
    ]);
};

export default useAutoScrollOnTimeline;
